/**
 * DocShareModal — create + manage tracked document share links for a
 * Library asset. Opened from the asset detail drawer in
 * SalesLibraryPhase ("Share" button). Pairs with backend
 * routes/docShares.js:
 *   POST   /api/docs/share          create (or reuse) a link
 *   GET    /api/docs/links/:assetId list links for the asset
 *   DELETE /api/docs/links/:id      revoke a link
 *
 * In-app SELLER surface — uses var(--room-*) tokens with light
 * fallbacks per CLAUDE.md theming rules (never hardcoded neutrals).
 * Semantic status chips stay hardcoded as self-consistent pairs.
 */

import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const EXPIRY_OPTIONS = [
  { value: '', label: 'None' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

export default function DocShareModal({ asset, authToken, onClose }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [expiry, setExpiry] = useState('');           // '' = no expiry
  const [allowDownload, setAllowDownload] = useState(true);
  const [pinned, setPinned] = useState(false);        // OFF: link tracks latest
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);       // { url, reused }
  const [copied, setCopied] = useState(false);

  const [links, setLinks] = useState(null);
  const [linksError, setLinksError] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  const loadLinks = async () => {
    setLinksError(null);
    try {
      const r = await fetch(`${API_BASE}/api/docs/links/${asset.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Could not load links (${r.status})`);
      setLinks(Array.isArray(body.links) ? body.links : Array.isArray(body) ? body : []);
    } catch (err) {
      setLinksError(err.message);
      setLinks([]);
    }
  };
  useEffect(() => { loadLinks(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [asset.id]);

  const submit = async () => {
    const em = email.trim();
    if (!em) { setError('Recipient email is required.'); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/docs/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          libraryAssetId: asset.id,
          email: em,
          firstName: firstName.trim() || null,
          expiresInDays: expiry ? Number(expiry) : null,
          allowDownload,
          version: pinned ? asset.currentVersion : null,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Share failed (${r.status})`);
      setCreated({ url: body.url, reused: !!body.reused });
      setCopied(false);
      loadLinks();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    if (!created?.url) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Clipboard API can be unavailable (http, older browsers) —
      // fall back to a transient textarea + execCommand.
      try {
        const ta = document.createElement('textarea');
        ta.value = created.url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (_e) { /* leave the URL selectable for manual copy */ }
    }
  };

  const revoke = async (linkId) => {
    if (!window.confirm('Revoke this link? The recipient will immediately lose access.')) return;
    setRevokingId(linkId);
    try {
      const r = await fetch(`${API_BASE}/api/docs/links/${linkId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Revoke failed (${r.status})`);
      }
      await loadLinks();
    } catch (err) {
      setLinksError(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        background: 'var(--room-card, #fff)', borderRadius: 14,
        width: 'min(640px, 100%)', maxHeight: '92vh', overflow: 'auto',
        padding: 22, boxShadow: '0 20px 60px rgba(15,23,42,0.3)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--room-text-strong, #0f172a)' }}>Share "{asset.title}"</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--room-text-muted, #64748b)', padding: 4 }}
          >
            ×
          </button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--room-text-muted, #64748b)' }}>
          Creates a personal tracked link — you'll see when they open it, which pages they read, and for how long.
        </p>

        {/* ── Create form ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ ...labelStyle, flex: '1 1 160px' }}>
              First name (optional)
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={busy}
                placeholder="e.g. Priya"
                style={inputStyle}
              />
            </label>
            <label style={{ ...labelStyle, flex: '2 1 220px' }}>
              Email <span style={{ color: '#dc2626' }}>*</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                placeholder="priya@prospect.com"
                style={inputStyle}
              />
            </label>
            <label style={{ ...labelStyle, flex: '1 1 120px' }}>
              Expires
              <select value={expiry} onChange={(e) => setExpiry(e.target.value)} disabled={busy} style={inputStyle}>
                {EXPIRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>

          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={allowDownload}
              onChange={(e) => setAllowDownload(e.target.checked)}
              disabled={busy}
            />
            Allow download
          </label>

          <div>
            <label style={checkboxRowStyle}>
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                disabled={busy}
              />
              Pin to current version (v{asset.currentVersion})
            </label>
            <div style={{ fontSize: 11.5, color: 'var(--room-text-subtle, #94a3b8)', marginLeft: 24, marginTop: 2 }}>
              Off: the link always serves the newest version.
            </div>
          </div>

          {error && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>
              {error}
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !email.trim()}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none',
                background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600,
                fontFamily: 'inherit',
                cursor: (busy || !email.trim()) ? 'not-allowed' : 'pointer',
                opacity: (busy || !email.trim()) ? 0.55 : 1,
              }}
            >
              {busy ? 'Creating…' : 'Create link'}
            </button>
          </div>

          {created && (
            <div style={{
              background: 'var(--room-surface, #f8fafc)', border: '1px solid var(--room-border, #e2e8f0)',
              borderRadius: 10, padding: 12,
            }}>
              {created.reused && (
                <div style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>
                  This person already had a link — reusing it.
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  readOnly
                  value={created.url || ''}
                  onFocus={(e) => e.target.select()}
                  style={{ ...inputStyle, flex: 1, fontSize: 12.5, color: 'var(--room-text-body, #334155)' }}
                />
                <button
                  type="button"
                  onClick={copyUrl}
                  style={{
                    padding: '8px 14px', borderRadius: 8,
                    border: '1px solid var(--room-border, #cbd5e1)',
                    background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)',
                    fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  {copied ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Existing links ── */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--room-text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
            Existing links
          </div>
          {linksError && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, marginBottom: 8 }}>
              {linksError}
            </div>
          )}
          {links === null ? (
            <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>Loading…</div>
          ) : links.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>No links yet for this asset.</div>
          ) : (
            links.map((l) => <LinkRow key={l.id} link={l} onRevoke={revoke} revoking={revokingId === l.id} />)
          )}
        </div>
      </div>
    </div>
  );
}

function LinkRow({ link, onRevoke, revoking }) {
  const expired = !!link.expiresAt && new Date(link.expiresAt).getTime() < Date.now();
  // Semantic status chips: pastel bg + dark ink, hardcoded as
  // self-consistent PAIRS per CLAUDE.md.
  const status = link.revokedAt
    ? { label: 'Revoked', bg: '#fee2e2', ink: '#991b1b' }
    : expired
      ? { label: 'Expired', bg: '#fef3c7', ink: '#92400e' }
      : { label: 'Active', bg: '#dcfce7', ink: '#166534' };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '9px 0', borderBottom: '1px solid var(--room-border-soft, #f1f5f9)', fontSize: 13,
    }}>
      <div style={{ minWidth: 0, flex: '1 1 180px' }}>
        <div style={{ color: 'var(--room-text-strong, #0f172a)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {link.stakeholderName || link.email}
        </div>
        {link.stakeholderName && (
          <div style={{ fontSize: 11.5, color: 'var(--room-text-muted, #64748b)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {link.email}
          </div>
        )}
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, background: status.bg, color: status.ink, padding: '2px 8px', borderRadius: 999 }}>
        {status.label}
      </span>
      <span style={{ fontSize: 12, color: 'var(--room-text-body, #475569)', whiteSpace: 'nowrap' }}>
        {link.viewCount ?? 0} view{(link.viewCount ?? 0) === 1 ? '' : 's'} · {link.downloadCount ?? 0} download{(link.downloadCount ?? 0) === 1 ? '' : 's'}
      </span>
      <span style={{ fontSize: 11.5, color: 'var(--room-text-muted, #64748b)', whiteSpace: 'nowrap' }}>
        {link.pinnedVersion == null ? 'latest' : `v${link.pinnedVersion}`}
      </span>
      <span style={{ fontSize: 11.5, color: 'var(--room-text-subtle, #94a3b8)', whiteSpace: 'nowrap' }}>
        {link.lastActivityAt
          ? `Last activity ${new Date(link.lastActivityAt).toLocaleDateString()}`
          : 'No activity yet'}
        {link.expiresAt && !link.revokedAt && ` · expires ${new Date(link.expiresAt).toLocaleDateString()}`}
      </span>
      {!link.revokedAt && (
        <button
          type="button"
          onClick={() => onRevoke(link.id)}
          disabled={revoking}
          style={{
            marginLeft: 'auto', padding: '4px 10px', borderRadius: 6,
            border: '1px solid #fecaca', background: 'var(--room-card, #fff)', color: '#b91c1c',
            fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
            cursor: revoking ? 'not-allowed' : 'pointer', opacity: revoking ? 0.55 : 1,
          }}
        >
          {revoking ? 'Revoking…' : 'Revoke'}
        </button>
      )}
    </div>
  );
}

const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontSize: 12, fontWeight: 600, color: 'var(--room-text-body, #475569)',
};
const inputStyle = {
  padding: '8px 12px', borderRadius: 8,
  border: '1px solid var(--room-border, #cbd5e1)',
  background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #0f172a)',
  fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const checkboxRowStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  fontSize: 13, color: 'var(--room-text-body, #475569)', cursor: 'pointer',
};
